import { useCallback, useEffect, useState } from "react";
import {
  getTikTokConnection,
  TikTokConnection,
} from "../../../../database/community/tiktokConnection";
import {
  getLinkedInConnection,
  LinkedInConnection,
} from "../../../../database/community/linkedinConnection";
import {
  getMetaConnection,
  MetaConnection,
} from "../../../../database/community/metaConnection";
import {
  getYouTubeConnection,
  YouTubeConnection,
} from "../../../../database/community/youtubeConnection";

// Single source for the first-party social connection state (TikTok, LinkedIn,
// Meta). Fetches once per mount, refreshes on window focus (so a connection
// made in the OAuth popup shows up when the coach returns), and exposes the
// derived booleans the publish buttons need. Replaces the duplicated
// fetch-on-focus blocks in MediaProjectsView and the connect panels.

export type SocialConnectionFlags = {
  tiktok: boolean;
  linkedin: boolean;
  instagram: boolean;
  facebook: boolean;
  youtube: boolean;
};

export type SocialConnections = {
  tiktok: TikTokConnection | null;
  linkedin: LinkedInConnection | null;
  meta: MetaConnection | null;
  youtube: YouTubeConnection | null;
  flags: SocialConnectionFlags;
  loading: boolean;
  refresh: () => Promise<void>;
};

export const useSocialConnections = (
  communityId?: string
): SocialConnections => {
  const [tiktok, setTiktok] = useState<TikTokConnection | null>(null);
  const [linkedin, setLinkedin] = useState<LinkedInConnection | null>(null);
  const [meta, setMeta] = useState<MetaConnection | null>(null);
  const [youtube, setYoutube] = useState<YouTubeConnection | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!communityId) {
      setTiktok(null);
      setLinkedin(null);
      setMeta(null);
      setYoutube(null);
      return;
    }
    setLoading(true);
    // Update each platform as soon as its request completes. One slow or
    // unavailable provider must not keep an already-connected provider's
    // publish controls disabled.
    const settle = <T,>(
      request: Promise<T>,
      apply: (value: T) => void
    ): Promise<void> =>
      request.then(apply).catch(() => undefined);
    await Promise.all([
      settle(getTikTokConnection(communityId), setTiktok),
      settle(getLinkedInConnection(communityId), setLinkedin),
      settle(getMetaConnection(communityId), setMeta),
      settle(getYouTubeConnection(communityId), setYoutube),
    ]);
    setLoading(false);
  }, [communityId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const flags: SocialConnectionFlags = {
    tiktok: Boolean(tiktok?.connected),
    linkedin: Boolean(linkedin?.connected),
    instagram: Boolean(meta?.connected && meta?.igUsername),
    facebook: Boolean(meta?.connected && meta?.pageName),
    youtube: Boolean(youtube?.connected),
  };

  return { tiktok, linkedin, meta, youtube, flags, loading, refresh };
};

export default useSocialConnections;
